/**
 * PDF处理工具 - 使用pdfjs-serverless
 * 专为Cloudflare Workers等边缘环境优化
 */

import { getDocument } from 'pdfjs-serverless';

export interface PDFInfo {
  text: string;
  totalPages: number;
  metadata?: any;
}

/**
 * 从PDF文件提取文本
 */
export async function extractTextFromPDF(file: File): Promise<PDFInfo> {
  try {
    // 读取文件为ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    
    // 加载PDF文档
    const document = await getDocument({
      data: new Uint8Array(arrayBuffer),
      useSystemFonts: true,
    }).promise;
    
    const totalPages = document.numPages;
    const metadata = await document.getMetadata();
    let fullText = '';
    
    // 提取每一页的文本
    for (let i = 1; i <= totalPages; i++) {
      const page = await document.getPage(i);
      const textContent = await page.getTextContent();
      
      // 拼接文本
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');
      
      fullText += `\n--- Page ${i} ---\n${pageText}`;
    }
    
    return {
      text: fullText.trim(),
      totalPages,
      metadata
    };
  } catch (error) {
    console.error('PDF extraction error:', error);
    throw new Error('PDF文本提取失败: ' + (error as Error).message);
  }
}

/**
 * 从PDF提取指定页数范围的文本
 */
export async function extractTextFromPDFRange(
  file: File,
  startPage: number = 1,
  endPage?: number
): Promise<PDFInfo> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const document = await getDocument({
      data: new Uint8Array(arrayBuffer),
      useSystemFonts: true,
    }).promise;
    
    const totalPages = document.numPages;
    const metadata = await document.getMetadata();
    const actualEndPage = endPage || totalPages;
    let fullText = '';
    
    for (let i = startPage; i <= Math.min(actualEndPage, totalPages); i++) {
      const page = await document.getPage(i);
      const textContent = await page.getTextContent();
      
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');
      
      fullText += `\n--- Page ${i} ---\n${pageText}`;
    }
    
    return {
      text: fullText.trim(),
      totalPages,
      metadata
    };
  } catch (error) {
    console.error('PDF extraction error:', error);
    throw new Error('PDF文本提取失败: ' + (error as Error).message);
  }
}

/**
 * 获取PDF基本信息(不提取全文)
 */
export async function getPDFInfo(file: File): Promise<{ pages: number; metadata: any }> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const document = await getDocument({
      data: new Uint8Array(arrayBuffer),
      useSystemFonts: true,
    }).promise;
    
    const metadata = await document.getMetadata();
    
    return {
      pages: document.numPages,
      metadata
    };
  } catch (error) {
    console.error('PDF info error:', error);
    throw new Error('获取PDF信息失败: ' + (error as Error).message);
  }
}
